import React, { useState, useEffect } from 'react';
import { Modal, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import illu4 from '../assets/illustrations/illu4.jpg';
import illu5 from '../assets/illustrations/illu5.jpg';
import illu6 from '../assets/illustrations/illu6.jpg';
import illu7 from '../assets/illustrations/illu7.png';
import illu8 from '../assets/illustrations/illu8.jpg';
import './GuideModal.css';

/**
 * Slides for Manager role (hierarchy level 2)
 * Intro slide + 4 steps covering the full workflow
 */
const MANAGER_SLIDES = [
    {
        number: '',
        title: 'Übersicht: Deine wichtigsten Arbeitsbereiche in TRAC',
        image: illu7,
        paragraphs: [
            <>
                <b>Actionboard</b><br />
                Hier hältst Du Handlungsfelder, Ziele und Maßnahmen aus der Befragung fest.
            </>,
            <>
                <b>Checkpoints</b><br />
                Hier gibst Du regelmäßig Rückmeldung zum Umsetzungsstand.
            </>
        ]
    },
    {
        number: '01',
        title: 'Lege Handlungsfelder & Ziele an',
        image: illu4,
        paragraphs: [
            <>
                <b>Welche Themen aus der Befragung willst Du angehen?</b>
            </>,
            <>
                <b>Im Actionboard</b> erfasst Du die relevanten Handlungsfelder Deines Teams und formulierst zu jedem ein klares Ziel.<br />
                Leitfragen unterstützen Dich bei der Konkretisierung.
            </>,
            <>
                <b>Empfehlung:</b> Fokus auf wenige, zentrale Themen.
            </>
        ]
    },
    {
        number: '02',
        title: 'Trage Maßnahmen ein',
        image: illu5,
        paragraphs: [
            <>
                Ergänze <b>im Actionboard</b> zu jedem Handlungsfeld Maßnahmen, die eure Ziele voranbringen, und lege Zuständigkeiten sowie Termine fest.
            </>,
            <>
                <b>Empfehlung:</b> 1–3 Maßnahmen pro Handlungsfeld.
            </>
        ]
    },
    {
        number: '03',
        title: 'Rückmeldungen zum Fortschritt',
        image: illu8,
        paragraphs: [
            <>
                In regelmäßigen Abständen werden <b>Rückmeldung zum Stand des Prozesses und der Maßnahmenumsetzung</b> in den Bereichen abgegeben.
            </>,
            <>
                Festgelegte Checkpoints geben die Rückmeldezeitpunkte vor.
            </>,
            <>
                Gib im Falle eigener Handlungsfelder & Maßnahmen selbst Rückmeldung zum Fortschritt auf Deiner <b>Checkpoint-Seite</b>.
            </>,
            <>
                Solltest Du kein eigenes Actionboard besitzen, siehst Du <b>alle Checkpoints in Deinem Dashboard</b>.
            </>
        ]
    },
    {
        number: '04',
        title: 'Bewerte die Zielerreichung',
        image: illu6,
        paragraphs: [
            <>
                Am Ende des Projektzeitraums schaust Du zurück
            </>,
            <>
                <b>Wurden die gesetzten Ziele erreicht?</b><br />
                <b>Hat sich das jeweilige Handlungsfeld verbessert?</b>
            </>,
            <>
                Die Reflektion am letzten Checkpoint hilft Dir und der Organisation aus dem Prozess zu lernen, <b>Erfolge sichtbar zu machen</b> und zu zeigen: Es hat sich etwas bewegt.
            </>
        ]
    }
];

/**
 * Slides for Lead role (hierarchy level 3)
 * Intro slide + 4 steps with read-only focus and dashboard overview
 */
const LEAD_SLIDES = [
    {
        number: '',
        title: 'Übersicht: Deine wichtigsten Arbeitsbereiche in TRAC',
        image: illu4,
        paragraphs: [
            <>
                <b>Actionboard</b><br />
                Hier kannst Du selbst Handlungsfelder und Maßnahmen aus der Befragung festhalten und siehst vertieft ein, an welchen Themen Dein Verantwortungsbereich arbeitet.
            </>,
            <>
                <b>Checkpoints</b><br />
                Hier siehst Du den Projektplan Deiner Organisation ein und gibst ggf. selbst Rückmeldung zum Umsetzungsstand.
            </>,
            <>
                <b>Dashboard</b><br />
                Hier behältst Du den Prozess- und Umsetzungsstand in Deinem Verantwortungsbereich gebündelt im Blick.
            </>
        ]
    },
    {
        number: '01',
        title: 'Lege selbst Handlungsfelder & Maßnahmen an',
        image: illu5,
        paragraphs: [
            <>
                Welche Themen aus der Befragung geht ihr an?
            </>,
            <>
                Nutze <b>Dein eigenes Actionboard</b>, um selbst Handlungsfelder und Maßnahmen für Deinen Verantwortungsbereich zu erfassen.
            </>,
            <>
                Leitfragen unterstützen Dich bei der Konkretisierung.
            </>,
            <>
                <i>Hinweis: Solltest Du keine Schreibrechte besitzen, wende Dich bitte an Deine internen Ansprechpersonen.</i>
            </>
        ]
    },
    {
        number: '02',
        title: 'Sieh alle Themen Deines Bereichs ein',
        image: illu8,
        paragraphs: [
            <>
                Im Actionboard besitzt Du <b>Leserechte für alle Eintragungen</b> Deines Verantwortungsbereichs.
            </>,
            <>
                Verschaffe Dir so einen vertieften Überblick über alle Handlungsfelder und geplanten Maßnahmen in den jeweiligen Teams.
            </>
        ]
    },
    {
        number: '03',
        title: 'Behalte den Zeitplan im Blick',
        image: illu6,
        paragraphs: [
            <>
                Auf der <b>Checkpoint-Seite</b> siehst Du alle <b>festgelegten Zeitpunkte</b> ein, zu denen Deine Organisation eine <b>Rückmeldung zum Prozessstand und der Maßnahmenumsetzung</b> in den Bereichen wünscht.
            </>,
            <>
                Gib im Falle eigener Handlungsfelder & Maßnahmen selbst Rückmeldung zum Fortschritt.
            </>
        ]
    },
    {
        number: '04',
        title: 'Behalte den Fortschritt gebündelt im Blick',
        image: illu7,
        paragraphs: [
            <>
                Auf dem Dashboard überblickst Du für Deinen gesamten Verantwortungsbereich, wie die Rückmeldungen zu den Checkpoints sind und wie die Umsetzung in den Teams/Bereichen verläuft.
            </>,
            <>
                So behältst Du den Prozesstand gebündelt im Blick und erkennst schnell, wo Unterstützungsbedarf besteht.
            </>
        ]
    }
];

/**
 * GuideModal Component
 * 
 * Displays a role-specific onboarding guide.
 * - Manager role (hierarchy level 2): 5-step full workflow guide
 * - Lead role (hierarchy level 3): 3-step read-only/overview guide
 * 
 * @param {boolean} show - Controls modal visibility
 * @param {function} onHide - Callback when modal is closed
 * @param {number} userHierarchyLevel - User's role hierarchy level (default: 2 for Manager)
 */
const GuideModal = ({ show, onHide, userHierarchyLevel = 2 }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const navigate = useNavigate();

    // Select slides based on user's hierarchy level
    // Level 3 (Lead) gets the full workflow guide
    // Level 2 and below (Manager, Admin, Super Admin) gets the read-only/overview guide
    const slides = userHierarchyLevel >= 3 ? MANAGER_SLIDES : LEAD_SLIDES;

    useEffect(() => {
        if (show) {
            setCurrentSlide(0);
        }
    }, [show]);

    const handleNext = () => {
        if (currentSlide < slides.length - 1) {
            setCurrentSlide(currentSlide + 1);
        }
    };

    const handleBack = () => {
        if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1);
        } else {
            // On first slide, close the modal
            onHide();
        }
    };

    const handleComplete = () => {
        // Close modal first, then navigate to the actionboard
        onHide();
        navigate('/actionboard');
    };

    const slide = slides[currentSlide];
    const isFirstSlide = currentSlide === 0;
    const isLastSlide = currentSlide === slides.length - 1;

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="xl"
            centered
            className="guide-modal"
        >
            <Modal.Body className="guide-modal-body">
                {/* Red accent bar at top */}
                {/* <div className="guide-accent-top"></div> */}

                {/* Red accent rectangle - top center */}
                {/* <div className="guide-accent-center"></div> */}

                {/* Red accent rectangle - top right */}
                {/* <div className="guide-accent-top-right"></div> */}

                <Row className="guide-content-row">
                    {/* Left Content Column */}
                    <Col lg={6} className="guide-content-col">
                        <div className="guide-content">
                            <div className="slide-number">{slide.number}</div>
                            <h2 className="slide-title">{slide.title}</h2>
                            <div className="slide-content">
                                {slide.paragraphs.map((paragraph, idx) => (
                                    <p key={idx}>{paragraph}</p>
                                ))}
                            </div>
                            <div className="slide-buttons">
                                {isLastSlide ? (
                                    <>
                                        <button
                                            type="button"
                                            className="guide-back-button"
                                            onClick={handleBack}
                                        >
                                            zurück
                                        </button>
                                        <button
                                            type="button"
                                            className="guide-next-button guide-button-wide"
                                            onClick={handleComplete}
                                        >
                                            zum Actionboard
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            className="guide-back-button"
                                            onClick={handleBack}
                                        >
                                            zurück
                                        </button>
                                        <button
                                            type="button"
                                            className="guide-next-button"
                                            onClick={handleNext}
                                        >
                                            Weiter
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </Col>

                    {/* Right Image Column */}
                    <Col lg={6} className="guide-image-col">
                        <div className="guide-image-container">
                            <img src={slide.image} alt="Guide illustration" className="guide-image" />
                            {/* Red accent rectangle - bottom right of image */}
                            {/*  <div className="guide-accent-image-right"></div> */}
                        </div>
                    </Col>
                </Row>

                {/* Red accent rectangle - bottom left */}
                {/*<div className="guide-accent-bottom-left"></div>   */}
            </Modal.Body>
        </Modal>
    );
};

GuideModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    userHierarchyLevel: PropTypes.number
};

export default GuideModal;
